/**
 * operations.ts — turning a gesture into an Engine operation.
 *
 * This is the narrowest and most important file in the repository, because it
 * is where the client stops and the Engine starts.
 *
 * What the client is allowed to decide: WHICH operation the user asked for, and
 * WHICH objects it refers to. That is it.
 *
 * What the client must never decide, and does not decide here:
 *
 *   • the final grid-aligned position of anything — a dropped coordinate is a
 *     request, and the Engine returns the resolved origin in the projection;
 *   • whether two ports may connect, or which port to use when the user aimed
 *     at a body;
 *   • the trace domain or kind — those are derived from the resolved ports, and
 *     sending them from here would be the browser asserting an engineering
 *     fact it does not own;
 *   • the route a trace takes.
 *
 * Every operation below is submitted to `authoring/preview` first (§12). The
 * preview response is the Engine's answer, and it is what the user approves.
 */

import type { AuthoringOperationInput } from "../../types/contract";

/** The Engine's id grammar. An id it would reject is refused here, not sent. */
const AUTHORING_ID = /^[A-Za-z0-9_-]{1,64}$/;

export function isAuthoringId(value: string): boolean {
  return AUTHORING_ID.test(value);
}

/**
 * A short, unique, grammar-legal id.
 *
 * Random rather than sequential because two engineers working on the same
 * diagram must not generate the same instance id; short enough to read in a
 * plan diff, which is where a human reviews it.
 */
export function newId(prefix: string): string {
  const random = Math.random().toString(36).slice(2, 8);
  const stamp = Date.now().toString(36).slice(-5);
  return `${prefix}-${stamp}${random}`.slice(0, 64);
}

let operationCounter = 0;
export function newOperationId(): string {
  operationCounter += 1;
  return `op-${Date.now().toString(36)}-${operationCounter.toString(36)}`;
}

/**
 * One reviewed mutation, with the Engine's own discriminator inside the command.
 *
 * `type` appears TWICE on the wire, in two different places with two different
 * readers, and omitting either is a refusal:
 *
 *   • the OPERATION's `type` is the address the plan envelope dispatches on;
 *   • the COMMAND's `type` is the discriminant the Engine's command module
 *     reads (`parseDiagramCommand` refuses any command whose `type` is absent
 *     or does not match, with `invalid-or-mismatched-diagram-command`).
 *
 * This file omitted the second one, so every placement, move, rotation,
 * connection and object creation the workbench sent was refused by the Engine
 * and the refusal was displayed verbatim — which is what a refusal-only path
 * looks like while passing every test that only checks the refusal arrived.
 *
 * Both values are the same string this function already receives, and it is the
 * operation type the Engine registered, not one written here: every call site
 * passes a type the Engine's manifest publishes, and `authoringBoundary.test.ts`
 * pins those against the published enumeration.
 */
function operation(type: string, command: Record<string, unknown>): AuthoringOperationInput {
  return { operationId: newOperationId(), type, command: { type, ...command } };
}

// ---------------------------------------------------------------------------
// Placement
// ---------------------------------------------------------------------------

export interface PlaceAtRequest {
  diagramId: string;
  defId: string;
  objectId: string;
  symbolInstanceId?: string;
  /** Where the user dropped it. Must already be on the display grid. */
  atMm: { x: number; y: number };
  rotation?: 0 | 90 | 180 | 270;
  /** The manifest alias, when the definition comes from the controlled registry. */
  symbolDependency?: string;
}

/**
 * Place at an exact coordinate.
 *
 * The exact primitive, not an intent, because the user pointed at a spot. The
 * Engine REJECTS an off-grid coordinate rather than snapping it — so the client
 * snaps for the ghost and sends the snapped value, and a mismatch surfaces as a
 * refusal instead of a silent 0.3 mm drift.
 */
export function placeAt(request: PlaceAtRequest): AuthoringOperationInput {
  return operation("diagram/symbol/add", {
    diagramId: request.diagramId,
    symbolInstanceId: request.symbolInstanceId ?? newId("sym"),
    defId: request.defId,
    objectId: request.objectId,
    x: request.atMm.x,
    y: request.atMm.y,
    rotation: request.rotation ?? 0,
    ...(request.symbolDependency ? { symbolDependency: request.symbolDependency } : {}),
  });
}

/**
 * Place relative to something already on the sheet.
 *
 * The intent form, used when the user says "to the right of P-101" rather than
 * pointing. The Engine resolves spacing, collision and grid. The relation is
 * SPATIAL and says nothing about process flow — `right_of` is not `downstream`.
 */
export function placeRelative(request: {
  diagramId: string;
  defId: string;
  objectId: string;
  relativeTo: string;
  relation: "left_of" | "right_of" | "above" | "below";
  symbolInstanceId?: string;
  rotation?: 0 | 90 | 180 | 270;
  spacingMm?: number;
}): AuthoringOperationInput {
  return operation("diagram/symbol/place-relative", {
    diagramId: request.diagramId,
    symbolInstanceId: request.symbolInstanceId ?? newId("sym"),
    defId: request.defId,
    objectId: request.objectId,
    relativeTo: request.relativeTo,
    relation: request.relation,
    ...(request.rotation !== undefined ? { rotation: request.rotation } : {}),
    ...(request.spacingMm !== undefined ? { spacingMm: request.spacingMm } : {}),
  });
}

export function placeInRegion(request: {
  diagramId: string;
  defId: string;
  objectId: string;
  region: string;
  symbolInstanceId?: string;
  rotation?: 0 | 90 | 180 | 270;
}): AuthoringOperationInput {
  return operation("diagram/symbol/place-region", {
    diagramId: request.diagramId,
    symbolInstanceId: request.symbolInstanceId ?? newId("sym"),
    defId: request.defId,
    objectId: request.objectId,
    region: request.region,
    ...(request.rotation !== undefined ? { rotation: request.rotation } : {}),
  });
}

// ---------------------------------------------------------------------------
// Moving and rotating
// ---------------------------------------------------------------------------

export function moveTo(diagramId: string, symbolInstanceId: string, toMm: { x: number; y: number }): AuthoringOperationInput {
  return operation("diagram/symbol/move", { diagramId, symbolInstanceId, x: toMm.x, y: toMm.y });
}

export function rotate(diagramId: string, symbolInstanceId: string, rotation: 0 | 90 | 180 | 270): AuthoringOperationInput {
  return operation("diagram/symbol/rotate", { diagramId, symbolInstanceId, rotation });
}

export function remove(diagramId: string, symbolInstanceId: string): AuthoringOperationInput {
  return operation("diagram/symbol/remove", { diagramId, symbolInstanceId });
}

// ---------------------------------------------------------------------------
// Connecting
// ---------------------------------------------------------------------------

/**
 * Connect two ports the user pointed at.
 *
 * `diagram/connect` rather than `diagram/trace/add`: the intent form lets the
 * Engine resolve the endpoints, check compatibility, derive domain and kind
 * from the resolved ports, and route. No `traceDomain` or `traceKind` is sent —
 * a browser that named them would be deciding what kind of line this is.
 */
export function connectPorts(request: {
  diagramId: string;
  from: { symbolId: string; portId: string };
  to: { symbolId: string; portId: string };
  traceId?: string;
}): AuthoringOperationInput {
  return operation("diagram/connect", {
    diagramId: request.diagramId,
    traceId: request.traceId ?? newId("t"),
    from: { symbolInstanceId: request.from.symbolId, portId: request.from.portId },
    to: { symbolInstanceId: request.to.symbolId, portId: request.to.portId },
  });
}

/**
 * Connect by SEMANTIC ROLE rather than by exact port.
 *
 * Used when the user connects two bodies without aiming at ports. The Engine
 * resolves which ports these roles mean, and refuses with candidates when the
 * answer is ambiguous — which is the correct outcome, and far better than the
 * client picking the nearest one.
 */
export function connectByRole(request: {
  diagramId: string;
  from: { symbolId: string; portRole: string };
  to: { symbolId: string; portRole: string };
  traceId?: string;
}): AuthoringOperationInput {
  return operation("diagram/connect", {
    diagramId: request.diagramId,
    traceId: request.traceId ?? newId("t"),
    from: { symbolInstanceId: request.from.symbolId, portRole: request.from.portRole },
    to: { symbolInstanceId: request.to.symbolId, portRole: request.to.portRole },
  });
}

// ---------------------------------------------------------------------------
// Inserting into a line
// ---------------------------------------------------------------------------

/**
 * Insert a component into an existing line — one atomic operation.
 *
 * The user drags a valve onto a pipe. What has to happen is: retire the trace,
 * place the body, create two traces carrying the original line's authored
 * identity, and route both. Doing that as four client-issued operations would
 * put a broken graph in the plan between steps. `diagram/insert-inline` is one
 * operation precisely so it cannot.
 */
export function insertInline(request: {
  diagramId: string;
  traceId: string;
  defId: string;
  objectId: string;
  symbolInstanceId?: string;
  rotation?: 0 | 90 | 180 | 270;
  /** Where along the line, 0..1. The Engine defaults to the midpoint. */
  fraction?: number;
}): AuthoringOperationInput {
  return operation("diagram/insert-inline", {
    diagramId: request.diagramId,
    traceId: request.traceId,
    symbolInstanceId: request.symbolInstanceId ?? newId("sym"),
    defId: request.defId,
    objectId: request.objectId,
    upstreamTraceId: newId("tu"),
    downstreamTraceId: newId("td"),
    ...(request.rotation !== undefined ? { rotation: request.rotation } : {}),
    ...(request.fraction !== undefined ? { fraction: request.fraction } : {}),
  });
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function align(diagramId: string, symbolInstanceIds: string[], axis: "horizontal" | "vertical", edge: "min" | "center" | "max"): AuthoringOperationInput {
  return operation("diagram/layout/align", { diagramId, symbolInstanceIds, axis, edge });
}

export function distribute(diagramId: string, symbolInstanceIds: string[], axis: "horizontal" | "vertical", spacingMm?: number): AuthoringOperationInput {
  return operation("diagram/layout/distribute", {
    diagramId, symbolInstanceIds, axis,
    ...(spacingMm !== undefined ? { spacingMm } : {}),
  });
}

// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------

/**
 * Create the engineering object a symbol will represent.
 *
 * A placement needs an object: a symbol on a sheet is a REPRESENTATION of
 * something the plant has, never a thing in itself. The workbench therefore
 * offers existing unplaced objects first and creates one only when the user
 * says this is something new.
 */
export function createObject(request: { objectId: string; objectType: string; tag?: string; name?: string }): AuthoringOperationInput {
  return operation("object/create", {
    objectId: request.objectId,
    objectType: request.objectType,
    ...(request.tag ? { tag: request.tag } : {}),
    ...(request.name ? { name: request.name } : {}),
  });
}
